'use client'

import countriesJsonData from '@components/data/countries/countries.json'
import React, { useState } from 'react'
import { Button } from 'app/_components/ui'
import cn from 'classnames'
import ReCAPTCHA from 'react-google-recaptcha'
import s from './Supplier.module.css'
import { validate as validateEmail } from 'email-validator'
import pageContent from 'pageContent/supplier-application.content.json'

export default function SupplierApplication() {
  // Content for this page is temporarily stored statically in a JSON file.
  // This allows us free up obsolete contentful models that were previously used for this page content.
  const {
    supplierData,
  }: {
    supplierData: any
  } = pageContent

  /* API path */
  const apiPath = '/api/customer/form'

  /* Button Loading true/false */
  const [loading, setLoading] = useState(false)

  /* Recaptcha Ref */
  const recaptchaRef = React.useRef(null)

  /* Country List */
  const countryList: any = []
  countriesJsonData.map((item: any, index: number) => {
    countryList.push(
      <option key={index} value={item.name}>
        {item.name}
      </option>
    )
  })

  /* Form submited message */
  const [formSubmittedSuccess, setFormSubmittedSuccess] = useState('')
  const [formSubmittedError, setFormSubmittedError] = useState('')

  const stateData: any = {
    firstName: '',
    lastName: '',
    companyName: '',
    website: '',
    email: '',
    country: '',
    state: '',
    productsType: '',
    productsOffered: '',
    factoryCount: '',
    additionalComment: '',
  }
  const [formState, setFormState] = useState({ stateData }) as any

  const stateErrData: any = {
    firstName: '',
    lastName: '',
    companyName: '',
    website: '',
    email: '',
    country: '',
    state: '',
    productsType: '',
    productsOffered: '',
    factoryCount: '',
    additionalComment: '',
    captcha: '',
  }
  const [formErrorState, setFormErrorState] = useState({ stateErrData }) as any
  const [isValidCaptcha, setIsValidCaptcha] = useState(false) as any

  const inputChange = (event: any) => {
    const stateData = formState.stateData
    stateData[event.target.name] = event.target.value
    setFormState({ ...formState, stateData })
  }

  const onReCAPTCHAChange = async (captchaCode) => {
    // If the reCAPTCHA code is null or undefined indicating that
    // the reCAPTCHA was expired then return early
    if (!captchaCode) {
      return
    }
    try {
      const response = await fetch('/api/captcha/validate', {
        method: 'POST',
        body: JSON.stringify({
          email: formState.stateData.email,
          captcha: captchaCode,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      })
      if (response.ok) {
        // If the response is ok than show the success alert
        setIsValidCaptcha(true)
      } else {
        // Else throw an error with the message returned
        // from the API
        const error = await response.json()
        throw new Error(error.message)
      }
    } catch (error) {
      recaptchaRef.current.reset()
      const stateErrData = formErrorState.stateErrData
      stateErrData.captcha = error?.message || 'Something went wrong'
      setFormErrorState({ ...formErrorState, stateErrData })
    } finally {
      // Reset the reCAPTCHA when the request has failed or succeeeded
      // so that it can be executed again if user submits another email.
    }
  }

  /* Submit Form */
  const formSubmit = async (event: any) => {
    event.preventDefault()
    setFormSubmittedSuccess('')
    setFormSubmittedError('')
    if (validate()) {
      const getStateData = formState.stateData
      const firstName: string = getStateData.firstName
      const lastName: string = getStateData.lastName
      const email: string = getStateData.email
      const company: string = getStateData.companyName
      let customerId = 0

      try {
        setLoading(true)

        /* Check customer is exist OR not */
        const findCustomer = await fetch(`${apiPath}?email=${email}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        })
          .then((response) => response.json())
          .then((response) => {
            return response.id
          })

        /* Create OR update customer */
        if (findCustomer == '') {
          const createCustomer = await fetch(apiPath, {
            method: 'POST',
            body: JSON.stringify({ firstName, lastName, email, company }),
            headers: { 'Content-Type': 'application/json' },
          })
            .then((response) => response.json())
            .then((response) => {
              return response.id
            })
          customerId = createCustomer
        } else {
          const id = findCustomer
          const updateCustomer = await fetch(apiPath, {
            method: 'PUT',
            body: JSON.stringify({ id, firstName, lastName, email, company }),
            headers: { 'Content-Type': 'application/json' },
          })
            .then((response) => response.json())
            .then((response) => {
              return response.id
            })
          customerId = updateCustomer
        }

        /* Update Customer Form Field (it has limit for parameters, so we split into 2 API call) */
        const updateFormData: any = [
          {
            customer_id: customerId,
            name: 'Supplier Website',
            value: getStateData.website,
          },
          {
            customer_id: customerId,
            name: 'Supplier Country',
            value: getStateData.country,
          },
          {
            customer_id: customerId,
            name: 'Supplier State',
            value: getStateData.state,
          },
          {
            customer_id: customerId,
            name: 'Supplier Products Type',
            value: getStateData.productsType,
          },
          {
            customer_id: customerId,
            name: 'Supplier Products Offered',
            value: getStateData.productsOffered,
          },
          {
            customer_id: customerId,
            name: 'Supplier Factory Count',
            value: getStateData.factoryCount,
          },
          {
            customer_id: customerId,
            name: 'Supplier Additional Comment',
            value: getStateData.additionalComment,
          },
        ]
        await fetch(apiPath, {
          method: 'PATCH',
          body: JSON.stringify(updateFormData),
          headers: { 'Content-Type': 'application/json' },
        })

        fetch(`/api/customer/supplierform`, {
          method: 'POST',
          body: JSON.stringify(formState.stateData),
          headers: { 'Content-Type': 'application/json' },
        })

        setLoading(false)
        setFormSubmittedSuccess(
          "Thank you for submitting the application, we'll be in touch soon."
        )
      } catch (error) {
        setLoading(false)
        setFormSubmittedError(
          'You have encountered an error please try again later after some time.'
        )
      }

      event.target.reset()
      recaptchaRef.current.reset()
      setIsValidCaptcha(false)
      setFormState({ ...formState, stateData })
      setFormErrorState({ ...formErrorState, stateErrData })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  /* Form validation */
  const validate = () => {
    const stateErrData = formErrorState.stateErrData
    const stateData = formState.stateData
    let isValid = true
    const textBoxMsg = 'This is a required field.'
    const selectMsg = 'Please select an option.'
    const emailMsg =
      'Please enter a valid email address. For example johndoe@domain.com. '
    const captchaMsg = 'Please select captcha checkbox'

    if (!stateData.firstName) {
      isValid = false
      stateErrData.firstName = textBoxMsg
    } else {
      stateErrData.firstName = ''
    }

    if (!isValidCaptcha) {
      isValid = false
      stateErrData.captcha = captchaMsg
    } else {
      stateErrData.captcha = ''
    }

    if (!stateData.lastName) {
      isValid = false
      stateErrData.lastName = textBoxMsg
    } else {
      stateErrData.lastName = ''
    }

    if (!stateData.companyName) {
      isValid = false
      stateErrData.companyName = textBoxMsg
    } else {
      stateErrData.companyName = ''
    }

    if (!stateData.website) {
      isValid = false
      stateErrData.website = textBoxMsg
    } else {
      stateErrData.website = ''
    }

    if (!stateData.email) {
      isValid = false
      stateErrData.email = textBoxMsg
    } else if (stateData.email !== '' && !validateEmail(stateData.email)) {
      isValid = false
      stateErrData.email = emailMsg
    } else {
      stateErrData.email = ''
    }

    if (!stateData.country) {
      isValid = false
      stateErrData.country = selectMsg
    } else {
      stateErrData.country = ''
    }

    if (!stateData.state) {
      isValid = false
      stateErrData.state = textBoxMsg
    } else {
      stateErrData.state = ''
    }

    if (!stateData.productsType) {
      isValid = false
      stateErrData.productsType = selectMsg
    } else {
      stateErrData.productsType = ''
    }

    if (!stateData.productsOffered) {
      isValid = false
      stateErrData.productsOffered = textBoxMsg
    } else {
      stateErrData.productsOffered = ''
    }

    if (!stateData.factoryCount) {
      isValid = false
      stateErrData.factoryCount = selectMsg
    } else {
      stateErrData.factoryCount = ''
    }

    setFormErrorState({ ...formErrorState, stateErrData })

    return isValid
  }

  return (
    <div
      className={cn(s.container, s.supplierFormMain)}
      style={{
        background: `url('${supplierData.fields.backgroundImage.fields.file.url}') no-repeat`,
      }}
    >
      <div className={s.supplierAppMain}>
        {formSubmittedSuccess != '' && (
          <ul className={s.successMessage}>
            <li>
              <span>{formSubmittedSuccess}</span>
            </li>
          </ul>
        )}
        {formSubmittedError != '' && (
          <ul className={s.errorMessage}>
            <li>
              <span>{formSubmittedError}</span>
            </li>
          </ul>
        )}

        <div className={cn(s.mainForm, s.supplierForm)}>
          <h1 className={s.feedbackTitle}>
            {supplierData.fields.sectionHeading}
          </h1>
          <div className={s.feedbackDesc}>
            {supplierData.fields.sectionDescription.content &&
              supplierData.fields.sectionDescription.content.map(
                (item: any, index: number) => {
                  return (
                    <p key={index}>
                      {item.content.map(
                        (contentsItem: any, contentsIndex: number) => {
                          return (
                            <span key={contentsIndex}>
                              {contentsItem.value}
                            </span>
                          )
                        }
                      )}
                    </p>
                  )
                }
              )}
          </div>
          <form
            className={s.supplierApplicationForm}
            onSubmit={(event) => {
              formSubmit(event)
            }}
          >
            <div className={s.feedbackMain}>
              <div className={s.feedbackFrmIcon}>
                <img
                  className={s.BadgeImage}
                  src={supplierData.fields.badgeImage.fields.file.url}
                  alt={supplierData.fields.badgeImage.fields.title}
                />
              </div>
              <div className={s.feedbackFormInner}>
                <div className={cn(s.fullRow, s.multiInput)}>
                  <div className={s.colInput}>
                    <span className={s.requireStar}>*</span>
                    <input
                      type="text"
                      name="firstName"
                      placeholder={supplierData.fields.firstNameTitle}
                      className={cn(
                        s.inputText,
                        formErrorState.stateErrData.firstName && s.fieldError
                      )}
                      onChange={(event) => {
                        inputChange(event)
                      }}
                    />
                    {formErrorState.stateErrData.firstName && (
                      <span className={s.error}>
                        {formErrorState.stateErrData.firstName}
                      </span>
                    )}
                  </div>
                  <div className={s.colInput}>
                    <span className={s.requireStar}>*</span>
                    <input
                      type="text"
                      name="lastName"
                      placeholder={supplierData.fields.lastNameTitle}
                      className={cn(
                        s.inputText,
                        formErrorState.stateErrData.lastName && s.fieldError
                      )}
                      onChange={(event) => {
                        inputChange(event)
                      }}
                    />
                    {formErrorState.stateErrData.lastName && (
                      <span className={s.error}>
                        {formErrorState.stateErrData.lastName}
                      </span>
                    )}
                  </div>
                </div>
                <div className={s.fullRow}>
                  <div className={s.colInput}>
                    <span className={s.requireStar}>*</span>
                    <input
                      type="text"
                      name="companyName"
                      placeholder={supplierData.fields.companyNameTitle}
                      className={cn(
                        s.inputText,
                        formErrorState.stateErrData.companyName && s.fieldError
                      )}
                      onChange={(event) => {
                        inputChange(event)
                      }}
                    />
                    {formErrorState.stateErrData.companyName && (
                      <span className={s.error}>
                        {formErrorState.stateErrData.companyName}
                      </span>
                    )}
                  </div>
                </div>
                <div className={s.fullRow}>
                  <div className={s.colInput}>
                    <span className={s.requireStar}>*</span>
                    <input
                      type="text"
                      name="website"
                      placeholder={supplierData.fields.websiteTitle}
                      className={cn(
                        s.inputText,
                        formErrorState.stateErrData.website && s.fieldError
                      )}
                      onChange={(event) => {
                        inputChange(event)
                      }}
                    />
                    {formErrorState.stateErrData.website && (
                      <span className={s.error}>
                        {formErrorState.stateErrData.website}
                      </span>
                    )}
                  </div>
                </div>
                <div className={s.fullRow}>
                  <div className={s.colInput}>
                    <span className={s.requireStar}>*</span>
                    <input
                      type="text"
                      name="email"
                      placeholder={supplierData.fields.emailAddressTitle}
                      className={cn(
                        s.inputText,
                        formErrorState.stateErrData.email && s.fieldError
                      )}
                      onChange={(event) => {
                        inputChange(event)
                      }}
                    />
                    {formErrorState.stateErrData.email && (
                      <span className={s.error}>
                        {formErrorState.stateErrData.email}
                      </span>
                    )}
                  </div>
                </div>
                <div className={cn(s.fullRow, s.multiInput)}>
                  <div
                    className={cn(
                      s.colInput,
                      s.selectWrapper,
                      formErrorState.stateErrData.country && s.colError
                    )}
                  >
                    <span className={s.requireStar}>*</span>
                    <select
                      className={cn(
                        s.selectBox,
                        formErrorState.stateErrData.country && s.fieldError
                      )}
                      name="country"
                      title={supplierData.fields.countryTitle}
                      onChange={(event) => {
                        inputChange(event)
                      }}
                    >
                      <option value="">
                        {supplierData.fields.countryTitle}
                      </option>
                      {countryList}
                    </select>
                    {formErrorState.stateErrData.country && (
                      <span className={s.error}>
                        {formErrorState.stateErrData.country}
                      </span>
                    )}
                  </div>
                  <div className={cn(s.colInput, s.col2NameInp)}>
                    <span className={s.requireStar}>*</span>
                    <input
                      type="text"
                      name="state"
                      placeholder={supplierData.fields.stateTitle}
                      className={cn(
                        s.inputText,
                        formErrorState.stateErrData.state && s.fieldError
                      )}
                      onChange={(event) => {
                        inputChange(event)
                      }}
                    />
                    {formErrorState.stateErrData.state && (
                      <span className={s.error}>
                        {formErrorState.stateErrData.state}
                      </span>
                    )}
                  </div>
                </div>
                <div className={s.fullRow}>
                  <div
                    className={cn(
                      s.colInput,
                      s.selectWrapper,
                      formErrorState.stateErrData.productsType && s.colError
                    )}
                  >
                    <span className={s.requireStar}>*</span>
                    <select
                      className={cn(
                        s.selectBox,
                        formErrorState.stateErrData.productsType && s.fieldError
                      )}
                      name="productsType"
                      title={supplierData.fields.productTypeTitle}
                      onChange={(event) => {
                        inputChange(event)
                      }}
                    >
                      <option value="">
                        {supplierData.fields.productTypeTitle}
                      </option>
                      {supplierData.fields.productTypeList &&
                        supplierData.fields.productTypeList.map(
                          (item: any, index: number) => {
                            return (
                              <option key={index} value={item.fields.blockName}>
                                {item.fields.blockName}
                              </option>
                            )
                          }
                        )}
                    </select>
                    {formErrorState.stateErrData.productsType && (
                      <span className={s.error}>
                        {formErrorState.stateErrData.productsType}
                      </span>
                    )}
                  </div>
                </div>
                <div className={s.fullRow}>
                  <div className={s.colInput}>
                    <span className={s.requireStar}>*</span>
                    <input
                      className={cn(
                        s.inputText,
                        formErrorState.stateErrData.productsOffered &&
                          s.fieldError
                      )}
                      type="text"
                      name="productsOffered"
                      placeholder={supplierData.fields.productOfferTitle}
                      onChange={(event) => {
                        inputChange(event)
                      }}
                    />
                    {formErrorState.stateErrData.productsOffered && (
                      <span className={s.error}>
                        {formErrorState.stateErrData.productsOffered}
                      </span>
                    )}
                  </div>
                </div>
                <div className={s.fullRow}>
                  <div
                    className={cn(
                      s.colInput,
                      s.selectWrapper,
                      formErrorState.stateErrData.factoryCount && s.colError
                    )}
                  >
                    <span className={s.requireStar}>*</span>
                    <select
                      className={cn(
                        s.selectBox,
                        formErrorState.stateErrData.factoryCount && s.fieldError
                      )}
                      name="factoryCount"
                      title={supplierData.fields.factoryCountTitle}
                      onChange={(event) => {
                        inputChange(event)
                      }}
                    >
                      <option value="">
                        {supplierData.fields.factoryCountTitle}
                      </option>
                      {supplierData.fields.factoryCountList &&
                        supplierData.fields.factoryCountList.map(
                          (item: any, index: number) => {
                            return (
                              <option key={index} value={item.fields.blockName}>
                                {item.fields.blockName}
                              </option>
                            )
                          }
                        )}
                    </select>
                    {formErrorState.stateErrData.factoryCount && (
                      <span className={s.error}>
                        {formErrorState.stateErrData.factoryCount}
                      </span>
                    )}
                  </div>
                </div>
                <div className={s.fullRow}>
                  <div className={s.colInput}>
                    <textarea
                      name="additionalComment"
                      placeholder={supplierData.fields.additionalCommentsTitle}
                      title={supplierData.fields.additionalCommentsTitle}
                      className={s.inputTextarea}
                      onChange={(event) => {
                        inputChange(event)
                      }}
                    ></textarea>
                  </div>
                </div>

                <div className={cn(s.fullRow, s.captchaSection)}>
                  <div className={s.captchaBox}>
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
                      onChange={onReCAPTCHAChange}
                    />
                    {formErrorState.stateErrData.captcha && (
                      <span className={s.error}>
                        {formErrorState.stateErrData.captcha}
                      </span>
                    )}
                  </div>
                </div>

                <span className={s.requireFlds}>
                  {supplierData.fields.requiredFieldsTitle}
                </span>
              </div>
              <div className={s.thankYoumsg}>
                <span>{supplierData.fields.thankYouMessage}</span>
              </div>
              <div className={s.buttonsSet}>
                <Button
                  className={cn(s.button, s.checkFormBtn)}
                  type="submit"
                  value={supplierData.fields.buttonLabel}
                  loading={loading}
                >
                  {supplierData.fields.buttonLabel}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
